import { truncateText } from "../../common/truncateText.js";
import { arrayOf, bool, string, shape, number } from "prop-types";
import { useState } from "react";

import cx from "classnames";

const propTypes = {
  displayImage: bool,
  isFirst: bool,
  variantType: number,
  columnCount: number,
  eyebrow: string,
  eyebrowUrl: string,
  title: string.isRequired,
  body: arrayOf(string),
  readTime: string,
  description: string,
  images: shape({
    mobile1X: string,
    desktop1X: string,
    mobile2X: string,
    desktop2X: string,
  }),
  imageAlt: string,
  contentUrl: string,
};

const ContentListingItem = (props) => {
  const {
    displayImage,
    isFirst,
    variantType,
    columnCount,
    eyebrow,
    eyebrowUrl,
    title,
    body,
    readTime,
    description,
    images,
    imageAlt,
    contentUrl,
  } = props;

  let imageSrcSet;

  const [mobile1XWidth, setMobile1XWidth] = useState(null);
  const [desktop1XWidth, setDesktop1XWidth] = useState(null);
  const [mobile2XWidth, setMobile2XWidth] = useState(null);
  const [desktop2XWidth, setDesktop2XWidth] = useState(null);

  if (images && variantType === 5) {
    // For variant 4 (carousel), we need to dynamically get the widths from each given image URL
    const mobile1XImg = new Image();
    const desktop1XImg = new Image();
    const mobile2XImg = new Image();
    const desktop2XImg = new Image();

    mobile1XImg.onload = function () {
      setMobile1XWidth(this.width);
    };

    desktop1XImg.onload = function () {
      setDesktop1XWidth(this.width);
    };

    mobile2XImg.onload = function () {
      setMobile2XWidth(this.width);
    };

    desktop2XImg.onload = function () {
      setDesktop2XWidth(this.width);
    };

    mobile1XImg.src = images.mobile1X;
    desktop1XImg.src = images.desktop1X;
    mobile2XImg.src = images.mobile2X;
    desktop2XImg.src = images.desktop2X;

    mobile1XWidth && desktop1XWidth && mobile2XWidth && desktop2XWidth
      ? (imageSrcSet = `
      ${images.mobile1X} ${mobile1XWidth}w,
      ${images.desktop1X} ${desktop1XWidth}w,
      ${images.mobile2X} ${mobile2XWidth}w,
      ${images.desktop2X} ${desktop2XWidth}w
    `)
      : null;
  } else if (images && variantType !== 5) {
    imageSrcSet = `
      ${images.mobile1X} 250w,
      ${images.desktop1X} 350w,
      ${images.mobile2X} 500w,
      ${images.desktop2X} 700w
    `;
  } else {
    imageSrcSet = null;
  }

  // const getImageSize = (url) =>
  //   new Promise((resolve, reject) => {
  //     const img = new Image();
  //     img.onload = () => resolve(img);
  //     img.onerror = (err) => reject(err);
  //     img.src = url;
  //   });
  // (async () => {
  //   const mobile1XSize = await getImageSize(images.mobile1X);
  //   const desktop1XSize = await getImageSize(images.desktop1X);
  //   const mobile2XSize = await getImageSize(images.mobile2X);
  //   const desktop2XSize = await getImageSize(images.desktop2X);
  //   const sizes = `
  //         ${images.mobile1X} ${mobile1XSize.width}
  //         ${images.desktop1X} ${desktop1XSize.width}
  //         ${images.mobile2X} ${mobile2XSize.width}
  //         ${images.desktop2X} ${desktop2XSize.width}
  //       `;
  // })();

  let imageSizes;
  if (variantType === 5) {
    imageSizes = `
      (min-width: 746px) calc((100vw - 70px - 60px) / 3), 
      (min-width: 680px) calc((100vw - 70px - 30px) / 2),
      calc(100vw - 70px)
    `;
  } else if (variantType === 3) {
    if (isFirst) {
      imageSizes = `
      (min-width: 746px) calc((100vw - 70px - 30px) / 2), 
      (min-width: 470px) calc(100vw - 70px),
      100vw 
      `;
    } else {
      imageSizes = `
      (min-width: 746px) 250px, 
      (min-width: 420px) 350px, 
      calc(100vw - 70px)
      `;
    }
  } else if (variantType === 2) {
    if (columnCount === 2) {
      imageSizes = `
      (min-width: 680px) calc((100vw - 70px - 30px) / 2),
      (min-width: 420px) 350px, 
      calc(100vw - 70px)
    `;
    } else {
      imageSizes = `
      (min-width: 1115px) 350px, 
      (min-width: 746px) calc((100vw - 70px - 60px) / 3), 
      (min-width: 680px) calc((100vw - 70px - 30px) / 2),
      (min-width: 420px) 350px, 
      calc(100vw - 70px)
    `;
    }
  } else {
    imageSizes = `
      (min-width: 1115px) 350px, 
      (min-width: 746px) calc((100vw - 70px - 60px) / 3), 
      (min-width: 680px) calc((100vw - 70px - 30px) / 2),
      (min-width: 420px) 350px, 
      calc(100vw - 70px)
    `;
  }

  return (
    <div
      className={cx("content-listing-item", {
        "content-listing-item--no-image": displayImage === false || !images,
      })}
    >
      <div className="content-listing-item__wrapper">
        {images && displayImage && (
          <div className="content-listing-item__image">
            {contentUrl ? (
              <a href={contentUrl}>
                <img
                  srcSet={imageSrcSet ? imageSrcSet : null}
                  sizes={imageSizes}
                  src={images.mobile1X}
                  alt={imageAlt ? imageAlt : ""}
                  loading="lazy"
                />
              </a>
            ) : (
              <img
                srcSet={imageSrcSet ? imageSrcSet : null}
                sizes={imageSizes}
                src={images.mobile1X}
                alt={imageAlt ? imageAlt : ""}
                loading="lazy"
              />
            )}
          </div>
        )}
        <div className="content-listing-item__text">
          {eyebrow && (
            <div className="content-listing-item__eyebrow">
              {eyebrowUrl ? (
                <a href={eyebrowUrl}>{eyebrow}</a>
              ) : (
                <span>{eyebrow}</span>
              )}
            </div>
          )}
          {title && (
            <div className="content-listing-item__title">
              {contentUrl ? (
                <a href={contentUrl}>{truncateText(title, 82)}</a>
              ) : (
                truncateText(title, 82)
              )}
            </div>
          )}
          {body && (
            <div className="content-listing-item__body">
              {body.map((text, i) => (
                <span key={i}>{text}&nbsp;</span>
              ))}
            </div>
          )}
          {readTime && (
            <div className="content-listing-item__read-time">{readTime}</div>
          )}
          {description && (
            <div
              className="content-listing-item__description"
              dangerouslySetInnerHTML={{ __html: description }}
            ></div>
          )}
        </div>
      </div>
    </div>
  );
};

// Specifies the default values for props:
ContentListingItem.defaultProps = {
  isFirst: false,
  variantType: 1,
  columnCount: 3,
};

ContentListingItem.propTypes = propTypes;
export default ContentListingItem;
